import { FeedbackType } from './Feedback'

export enum RequestType {
  SET_GOALS = 'set_goals',
  SET_GOALS_SKIPPED_EVALUATION = 'set_goals_skipped_evaluation',
  SET_GOALS_EARLY_EVALUATION = 'set_goals_early_evaluation',
  REVIEW_GOALS = 'review_goals',
  LEAVE_FEEDBACK = 'leave_feedback',
  SPECIFY_CLOSE_MEMBERS = 'specify_close_members',
  EVALUATE = 'evaluate',
  CLOSE_EVALUATION = 'close_evaluation',
  START_ONBOARDING = 'start_onboarding',
  PLAN_EVALUATIONS = 'plan_evaluations',
  COMPLETE_PROFILE = 'complete_profile',
  // Support-only, frontend-derived: guides finishing a freshly-created company.
  COMPLETE_COMPANY_PROFILE = 'complete_company_profile',
  // only dynamic request: when archived members not deleted for admin
  DELETE_ARCHIVED_MEMBERS = 'delete_archived_members',
  FILL_SURVEY = 'fill_survey',
}

// Request types that belong to the performance-management module (goals, feedback,
// evaluations). Used to gate badges/lists/reminders when the module is disabled.
export const PERFORMANCE_MANAGEMENT_REQUEST_TYPES: RequestType[] = [
  RequestType.SET_GOALS,
  RequestType.SET_GOALS_SKIPPED_EVALUATION,
  RequestType.SET_GOALS_EARLY_EVALUATION,
  RequestType.REVIEW_GOALS,
  RequestType.LEAVE_FEEDBACK,
  RequestType.SPECIFY_CLOSE_MEMBERS,
  RequestType.EVALUATE,
  RequestType.CLOSE_EVALUATION,
  RequestType.PLAN_EVALUATIONS,
  RequestType.COMPLETE_PROFILE,
  RequestType.DELETE_ARCHIVED_MEMBERS,
]

export interface IRequestPure {
  _id?: any
  memberId: string
  companyId?: string
  targetMemberId?: string
  notificationEmailSent: boolean
  customLead?: boolean
  feedbackId?: string
  evaluationId?: string
  teamId?: string
  actionType: RequestType
  completed: boolean
  dateCreated: Date
  dateCompleted?: Date
  termNumber: number
  // isFeedbackPlanned to be removed
  isFeedbackPlanned?: boolean
  feedbackType?: FeedbackType
  plannerId?: string
  // Engagement survey distribution this request asks the member to fill in
  distributionId?: string
}

export interface INewRequest extends IRequestPure {
  memberId: string
  notificationEmailSent: boolean
  actionType: RequestType
  completed: boolean
  dateCompleted?: Date
  termNumber: number
  targetMemberId?: string
  feedbackId?: string
  evaluationId?: string
  teamId?: string
  feedbackType?: FeedbackType
  pdpId?: string
  plannerId?: string
  companyId?: string
}
export interface INewCultureEvaluationRequest
  extends Pick<
    INewRequest,
    | 'memberId'
    | 'targetMemberId'
    | 'actionType'
    | 'notificationEmailSent'
    | 'completed'
    | 'dateCompleted'
    | 'dateCreated'
    | 'termNumber'
    | 'plannerId'
  > {}

export interface IPlanEvaluationsRequest
  extends Pick<
    IRequestPure,
    | 'memberId'
    | 'actionType'
    | 'notificationEmailSent'
    | 'completed'
    | 'dateCompleted'
    | 'dateCreated'
    | 'termNumber'
  > {
  actionType: RequestType.PLAN_EVALUATIONS
}

export const requestIsPlanEvaluations = (
  value: IRequestPure
): value is IReviewGoalsRequest => {
  return value.actionType === RequestType.PLAN_EVALUATIONS
}

export interface IOnboardingRequest extends INewRequest {
  durationInDays?: number
  completed: boolean
  dateCreated: Date
}

export const requestIsOnboardingRequest = (
  value: IRequestPure
): value is IOnboardingRequest => {
  return value.actionType === RequestType.START_ONBOARDING
}

export interface IReviewGoalsRequest extends IRequestPure {
  pdpId?: string
}
export const requestIsReviewGoals = (
  value: IRequestPure
): value is IReviewGoalsRequest => {
  return value.actionType === RequestType.REVIEW_GOALS
}

// Enriched by the requests business with the survey it points at
export interface IFillSurveyRequest extends IRequestPure {
  actionType: RequestType.FILL_SURVEY
  distributionId: string
  surveyName?: string
  periodEnd?: Date
}

export const requestIsFillSurvey = (
  value: IRequestPure
): value is IFillSurveyRequest => {
  return value.actionType === RequestType.FILL_SURVEY
}

// Requests whose action is a modal rather than a page. RequestModals renders them
// wherever they are opened from: the dashboard list or the activity panel.
export const requestOpensModal = (value: IRequestPure): boolean =>
  [
    RequestType.EVALUATE,
    RequestType.SPECIFY_CLOSE_MEMBERS,
    RequestType.DELETE_ARCHIVED_MEMBERS,
    RequestType.COMPLETE_COMPANY_PROFILE,
  ].includes(value.actionType)

export interface IEvaluationPlanningRequest extends IRequestPure {}

export const requestIsEvaluationPlanning = (
  value: IRequestPure
): value is IEvaluationPlanningRequest => {
  return value.actionType === RequestType.PLAN_EVALUATIONS
}

// This interface might be empty, but it is created to keep the naming convention for frontend and backend
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IRequest extends IRequestPure {}
